import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IDEOLOGY_AXES, type IdeologyVector } from '@/constants/ideology'
import { IdeologyComparison } from '@/components/ideology/IdeologyComparison'
import { Badge } from '@/components/ui/badge'

export interface FactionIdeologyComparisonProps {
  factionIdeology: IdeologyVector
  partyIdeology: IdeologyVector
  highlightDivergence?: boolean
  showLabels?: boolean
  compact?: boolean
  factionColor?: string
  partyColor?: string
  className?: string
}

// Threshold for highlighting an axis as divergent (|faction - party| > 40)
const DIVERGENCE_THRESHOLD = 40

/**
 * Find axes where faction diverges significantly from party.
 */
function getDivergentAxes(
  factionIdeology: IdeologyVector,
  partyIdeology: IdeologyVector
): { index: number; name: string; difference: number }[] {
  const divergent: { index: number; name: string; difference: number }[] = []

  IDEOLOGY_AXES.forEach((axis, index) => {
    const difference = Math.abs(factionIdeology[index] - partyIdeology[index])
    if (difference > DIVERGENCE_THRESHOLD) {
      divergent.push({ index, name: axis.label, difference })
    }
  })

  // Sort by divergence magnitude (highest first)
  return divergent.sort((a, b) => b.difference - a.difference)
}

/**
 * Component for comparing faction ideology vs party ideology.
 * Wraps the existing IdeologyComparison component with faction-specific styling
 * and divergence highlighting.
 */
export function FactionIdeologyComparison({
  factionIdeology,
  partyIdeology,
  highlightDivergence = true,
  showLabels = true,
  compact = false,
  factionColor = '#ef4444', // red for faction
  partyColor = '#3b82f6', // blue for party
  className,
}: FactionIdeologyComparisonProps) {
  const divergentAxes = highlightDivergence
    ? getDivergentAxes(factionIdeology, partyIdeology)
    : []

  return (
    <div className={cn('space-y-3', className)}>
      {/* Divergence warning badges */}
      {highlightDivergence && divergentAxes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {divergentAxes.map(({ name, difference }) => (
            <Badge
              key={name}
              variant="outline"
              className="gap-1 bg-orange-500/10 text-orange-500 border-orange-500/30"
            >
              <AlertTriangle className="h-3 w-3" />
              {name}: {difference}pt divergence
            </Badge>
          ))}
        </div>
      )}

      {/* Main ideology comparison */}
      <IdeologyComparison
        vectorA={factionIdeology}
        vectorB={partyIdeology}
        labelA="Faction"
        labelB="Party"
        colorA={factionColor}
        colorB={partyColor}
        showDistance={showLabels}
        showAxisDistances={showLabels}
        compact={compact}
      />
    </div>
  )
}

/**
 * Compact version showing just the key divergences.
 * Useful for list views or dashboard summaries.
 */
export interface FactionDivergenceSummaryProps {
  factionIdeology: IdeologyVector
  partyIdeology: IdeologyVector
  maxAxes?: number
  className?: string
}

export function FactionDivergenceSummary({
  factionIdeology,
  partyIdeology,
  maxAxes = 2,
  className,
}: FactionDivergenceSummaryProps) {
  const divergentAxes = getDivergentAxes(factionIdeology, partyIdeology)
  const displayAxes = divergentAxes.slice(0, maxAxes)

  if (displayAxes.length === 0) {
    return (
      <span className={cn('text-xs text-muted-foreground', className)}>
        Aligned with party
      </span>
    )
  }

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {displayAxes.map(({ name }) => (
        <Badge
          key={name}
          variant="outline"
          className="text-[10px] px-1.5 py-0 bg-orange-500/10 text-orange-500 border-orange-500/30"
        >
          {name}
        </Badge>
      ))}
      {divergentAxes.length > maxAxes && (
        <span className="text-[10px] text-muted-foreground">
          +{divergentAxes.length - maxAxes} more
        </span>
      )}
    </div>
  )
}
